import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRut, formatMoneyWithCurrency, formatUtcCalendarDate } from '../../../utils/formatter';
import { documentsDic } from '../../../utils/dataDictionary';
import { CurrencyIcon } from 'src/utils/currencyIcon';
import moment from 'moment';
import FilterTabs from '../../FilterTabs/FilterTabs';
import FactoringNotice from './FactoringNotice';
import AnnulmentNotice from './AnnulmentNotice';
import CreditDebitNotice from './CreditDebitNotice';
import MatchesTab from './MatchesTab';
import NominasTab from './NominasTab';
import CommentsTab from './CommentsTab';
import ParametrizacionTab from './ParametrizacionTab';
import ExportActions from '../../export/ExportActions';
import { getObligationTypeDisplayLabel } from 'src/components/general/js/forms/reconcile/components/matchRowHelpers';
import {
  getObligationDisplayAmount,
  isInternationalTcObligation,
} from 'src/components/tarjetasCredito/tcObligationHelpers';
import { dt, getDocumentDetailTabs } from 'src/utils/documentsI18n';
import './TaxDocumentDetail.css';

const ObligationsDetail = ({ data }) => {
  const { i18n } = useTranslation('documents');
  const [activeTab, setActiveTab] = useState('detalles');
  const isReceived = data.meta.isReceived;
  const isIntlTcObligation = isInternationalTcObligation(data);
  const obligationDisplayAmount = getObligationDisplayAmount(data);

  const commentCount = data.meta?.innerComments?.length || 0;
  const tabs = useMemo(
    () => getDocumentDetailTabs(commentCount),
    [commentCount, i18n.language]
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'detalles':
        return (
          <div className="document-content">
            {/* Document Stamp */}
            <div className="document-stamp">
              <div className="document-stamp-title">
                {getObligationTypeDisplayLabel(data.meta)}
              </div>
              <div className="document-stamp-number">
                {(data.meta.documentId || data.meta.number) ? `${dt('detail.docNumberPrefix')} ${data.meta.documentId || data.meta.number}` : dt('detail.noNumber')}
              </div>
              <div className="document-stamp-status">
                <span className={`status-badge ${
                  data.meta.isPaid 
                    ? 'paid' 
                    : data.meta?.isValid === false 
                      ? 'invalid' 
                      : 'pending'
                }`} style={{
                  ...(data.meta?.isValid === false && {
                    background: 'linear-gradient(135deg, #fef3f3 0%, #fde8e8 100%)',
                    border: '1px solid #f5c6cb',
                    color: '#991b1b'
                  })
                }}>
                  {data.meta?.isValid === false 
                    ? dt('detail.status.voided')
                    : data.meta.isPaid 
                      ? dt('detail.status.paid')
                      : dt('detail.status.pendingPayment')
                  }
                </span>
              </div>
            </div>
            
            {/* Main Info Grid */}
            <div className="document-grid">
              <div>
                <div className="info-block">
                  <div className="info-label">{dt('detail.type')}</div>
                  <div className="info-value" style={{ fontWeight: 600, fontSize: '1.2em' }}>
                    {getObligationTypeDisplayLabel(data.meta)}
                  </div>
                </div>
                <div className="info-block">
                  <div className="info-label">{isReceived ? dt('detail.issuedBy') : dt('detail.issuedTo')}</div>
                  <div className="counterpart-name" style={{ fontWeight: 800, fontSize: '1.1em' }}>
                    {isReceived ? data.meta.issuerName || dt('common.noCounterparty') : data.meta.receiverName || dt('common.noCounterparty')}
                  </div>
                  <div className="info-value">
                    <span className="secondary" style={{ fontWeight: 600 }}>
                      {formatRut(isReceived ? data.meta.issuerId : data.meta.receiverId) || ""}
                    </span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-block">
                    <div className="info-label">{dt('detail.issueDate')}</div>
                    <div className="info-value">
                      {formatUtcCalendarDate(data.documentDate) ?? '—'}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">{dt('detail.dueDate')}</div>
                    <div className="info-value">
                      {data.originalDueDate ? formatUtcCalendarDate(data.originalDueDate) ?? dt('detail.noInfo') : dt('detail.noInfo')}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">{dt('detail.paymentDate')}</div>
                    <div className="info-value">
                      {data?.meta?.isPaid === true && data.paymentDate ? moment(data.paymentDate.substr(0, 10)).format("DD/MM/YYYY") : dt('detail.noInfo')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Totals Card */}
            <div className="totals-card">
              {isIntlTcObligation && (
                <div className="totals-segment">
                  <div className="totals-label">MONTO</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(obligationDisplayAmount, 'CLP')}
                  </div>
                </div>
              )}
              {/* Exchange information - show first if exists and originalCurrency is not CLP */}
              {!isIntlTcObligation &&
                data.meta?.exchange?.originalCurrency &&
                data.meta.exchange.originalCurrency !== 'CLP' && (
                <>
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.originalCurrencyAmount', { currency: data.meta.exchange.originalCurrency })}</div>
                    <div className="totals-value">
                      {formatMoneyWithCurrency(
                        data.meta.exchange.originalAmount || 0, 
                        data.meta.exchange.originalCurrency
                      )}
                    </div>
                  </div>
                  {data.meta.exchange.exchangeRate && (
                    <div className="totals-segment">
                      <div className="totals-label">{dt('detail.exchangeType')}</div>
                      <div className="totals-value">
                        {data.meta.exchange.exchangeRate}
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* Fallback to old structure if exchange doesn't exist */}
              {!data.meta?.exchange?.originalCurrency && (
                <>
                  {data.meta?.currency && data.meta.currency !== 'CLP' && (
                    <div className="totals-segment">
                      <div className="totals-label">MONTO ORIGINAL ({data.meta.currency})</div>
                      <div className="totals-value">
                        {formatMoneyWithCurrency(
                          data.meta.originalAmount || 0, 
                          data.meta.currency
                        )}
                      </div>
                    </div>
                  )}
                  {data.meta?.currency && data.meta.currency === 'CLP' && !isIntlTcObligation && (
                    <div className="totals-segment">
                      <div className="totals-label">MONTO</div>
                      <div className="totals-value">
                        {formatMoneyWithCurrency(
                          data.meta.originalAmount || 0,
                          data.meta.currency,
                        )}
                      </div>
                    </div>
                  )}
                  { ![undefined, null, 0, 1, '0', '1', ''].includes(data.meta?.rate) && (
                    <div className="totals-segment">
                      <div className="totals-label">TASA</div>
                      <div className="totals-value">
                        {data.meta.rate}
                      </div>
                    </div>
                  )}
                  {data.meta?.localAmount && data.meta?.currency && data.meta.currency !== 'CLP' && (
                    <div className="totals-segment">
                      <div className="totals-label">{dt('detail.amountClp')}</div>
                      <div className="totals-value">
                        {formatMoneyWithCurrency(
                          data.meta.localAmount, 
                          'CLP'
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* Show MONTO (CLP) if exchange exists, hide MONTO if exchange exists */}
              {!isIntlTcObligation &&
                data.meta?.exchange?.originalCurrency &&
                data.meta.exchange.originalCurrency !== 'CLP' && (
                <div className="totals-segment">
                  <div className="totals-label">{dt('detail.amountClp')}</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(
                      data.meta.exchange.localAmount || 0, 
                      'CLP'
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Export Actions */}
            <ExportActions
              documentData={data}
              exportType="rendiciones"
              pdfUrl={data.meta.download?.pdf?.url || data.meta.download?.html?.url}
              xmlUrl={data.meta.download?.xml?.url}
              showPdf={!!(data.meta.download?.pdf?.url || data.meta.download?.html?.url)}
              showXml={!!data.meta.download?.xml?.url}
              showExcel={true}
            />

            {/* Factoring Notice */}
            {data.meta?.factoring?.cedido && (
              <FactoringNotice
                issuerName={data.meta.factoring.issuerName}
                cesionDate={data.meta.factoring.cesionDate ? moment(data.meta.factoring.cesionDate).format("DD/MM/YYYY") : null}
                downloadUrl={data.meta.download?.factoring?.url}
                cesiones={data.meta.factoring.cesiones}
              />
            )}

            {/* Annulment Notice */}
            {(data.meta?.isValid === false || data.meta?.isProtested === true) && (
              <AnnulmentNotice
                text={dt('detail.claimedNotice')}
                downloadUrl={null}
              />
            )}

            {/* No Incluir Notice */}
            {data.meta?.state === "NO_INCLUIR" && (
              <AnnulmentNotice
                text={dt("detail.noIncludeNotice")}
                downloadUrl={null}
                variant="noIncluir"
              />
            )}

            {/* Credit/Debit Notice */}
            {data.meta?.refDocuments && (data.meta?.refDocuments || []).length > 0 &&
              (data.meta?.refDocuments.map((refDocument, index) => {
                return (
                  <CreditDebitNotice
                    key={index}
                    refDocumentCode={documentsDic[parseInt(refDocument.documentCode)]?.fullName || dt("common.document")}
                    refDocumentId={refDocument.documentId}
                    downloadUrl={null}
                  />
                )
              })
            )}

            {/* Garantía fields - only shown when present (garantía obligations) */}
            {(data.meta?.login || data.meta?.transactionNumber || data.meta?.ticket || data.meta?.empresa?.name) && (
              <div className="totals-card totals-card--row">
                {data.meta?.login && (
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.login')}</div>
                    <div className="totals-value">{data.meta.login}</div>
                  </div>
                )}
                {data.meta?.transactionNumber && (
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.transactionNumber')}</div>
                    <div className="totals-value">{data.meta.transactionNumber}</div>
                  </div>
                )}
                {data.meta?.ticket && (
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.ticket')}</div>
                    <div className="totals-value">{data.meta.ticket}</div>
                  </div>
                )}
                {data.meta?.empresa?.name && (
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.empresa')}</div>
                    <div className="totals-value">{data.meta.empresa.name}</div>
                  </div>
                )}
              </div>
            )}

            {/* Glosa Section */}
            <div className="glosa-section">
              <div className="info-label">{dt('detail.glosa')}</div>
              {(() => {
                const filteredGlosa = data.meta?.detail?.filter(item => {
                  // Filter out rows where all relevant fields are null/empty
                  const hasDescription = item.description || item.name || item.item;
                  const hasUnitPrice = item.unit_price && item.unit_price !== 0;
                  const hasQuantity = item.quantity && item.quantity !== 0;
                  const hasTotalPrice = item.total_price && item.total_price !== 0;
                  
                  return hasDescription || hasUnitPrice || hasQuantity || hasTotalPrice;
                }) || [];

                return filteredGlosa.length > 0 ? (
                  <div className="glosa-table">
                    <div className="glosa-items">
                      {filteredGlosa.map((item, index) => (
                        <div key={index} className="glosa-item">
                          <div className="glosa-item-description">
                            {item.name?.trim() && item.description?.trim() ? (
                              <>
                                <div>{item.name.trim()}</div>
                                <div className="glosa-item-subdescription">{item.description.trim()}</div>
                              </>
                            ) : (
                              item.name?.trim() || item.description?.trim() || item.item || dt('common.noDescription')
                            )}
                          </div>
                          <div className="glosa-item-metrics">
                            <div className="glosa-metric">
                              <div className="glosa-metric-label">TOTAL</div>
                              <div className="glosa-metric-value">
                                {formatMoneyWithCurrency(
                                  item.total_price || (item.unit_price * (item.quantity || 1)) || 0, 
                                  data.currencyType,
                                  true,
                                  true
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="glosa-empty">
                    No hay glosa para mostrar
                  </div>
                );
              })()}
            </div>
          </div>
        );
      case 'matches':
        return <MatchesTab data={data} />;
      case 'nominas':
        return <NominasTab data={data} />;
      case 'parametrizacion':
        return <ParametrizacionTab data={data} />;
      case 'comentarios':
        return <CommentsTab data={data} />;
      default:
        return null;
    }
  };

  return (
    <div className="document-detail-container">
      {/* Tabs */}
      <FilterTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="document-filter-tabs"
      />

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ObligationsDetail; 